'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * AnimateIn — scroll-triggered fade + slide animation (Framer Motion).
 * Kept as a minimal client component so parent pages stay as Server Components.
 *
 * @param {{
 *   children: React.ReactNode,
 *   className?: string,
 *   direction?: "x" | "y",
 *   offset?: number,
 *   delay?: number,
 *   duration?: number,
 *   once?: boolean,
 *   amount?: number,
 *   trigger?: "inView" | "mount",
 * }} props
 */
export function AnimateIn({
    children,
    className,
    direction = 'y',
    offset = 24,
    delay = 0,
    duration = 0.55,
    once = true,
    amount = 0.1,
    trigger = 'inView',
    ...props
}) {
    const axis = direction === 'x' ? 'x' : 'y';
    const initial = { opacity: 0, [axis]: offset };
    const animate = { opacity: 1, [axis]: 0, willChange: 'auto' };
    const transition = { duration, delay, ease: [0.22, 1, 0.36, 1] };

    if (trigger === 'mount') {
        return (
            <motion.div
                initial={initial}
                animate={animate}
                transition={transition}
                className={cn(className)}
                {...props}
            >
                {children}
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={initial}
            whileInView={animate}
            viewport={{ once, amount, margin: '0px 0px 80px 0px' }}
            transition={transition}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
}

export default AnimateIn;
